import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Flex } from 'rebass';
import { Input, Button, Avatar, Typography, CheckBox } from '@lomadee/design-system';
import { Formik } from 'formik';

import { TeammateWrapper, RemoveTeammateIcon, StyledButton } from './styles';
import { OrganizationInviteTeammatesSchema } from './OrganizationRegistrationInviteTeammates.schema';
import { createAccountBrazilianDateToDBDate } from '@plug/helpers/date-helper';
import { useCreateOrganizationContext } from '@plug/contexts/CreateOrganizationContext';

import {
  SignUpWithOrganizationMutation,
  SignUpWithOrganizationUserGender,
  SignUpWithOrganizationVariables,
  SIGN_UP_WITH_ORGANIZATION,
} from '@plug/domains/auth/graphql/mutations/sign-up-with-organization';
import SIGN_IN_MUTATION from '@plug/domains/auth/graphql/mutations/signin-mutation';
import { useHistory } from 'react-router-dom';
import { useLogin } from '@plug/domains/auth/hooks/useLogin';
import { Spacer } from '@plug/ui';
import { TitleForm } from '../../../affiliate-registration/style';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const OrganizationRegistrationInviteTeammatesStep: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [signUpWithOrganization] = useMutation<
    SignUpWithOrganizationMutation,
    SignUpWithOrganizationVariables
  >(SIGN_UP_WITH_ORGANIZATION);
  const [signIn] = useMutation(SIGN_IN_MUTATION);
  const [loading, setLoading] = useState(false);
  const [teammates, setTeammates] = useState<string[]>([]);
  const { values, addValues, incompleteFlow } = useCreateOrganizationContext();
  const { login } = useLogin();

  const handleAddTeammateEmail = ({ email }: { email: string }, resetForm: () => void): void => {
    setTeammates([...teammates, email]);
    resetForm();
  };

  const handleRemoveTeammateEmail = (email: string): void => {
    let newTeammates = [...teammates];
    newTeammates = newTeammates.filter(teammate => teammate !== email);
    setTeammates(newTeammates);
  };

  const handleCreateOrganizationRequest = async (teammates?: string[]): Promise<void> => {
    if (!teammates?.length) addValues({ teammates: [] });

    setLoading(true);

    const cnpjOnlyNumbers = values.cnpj.replace(/\D/g, '');
    const formatedBirthday = createAccountBrazilianDateToDBDate(values.birthday);

    const variables: SignUpWithOrganizationVariables = {
      input: {
        email: values.email.toLowerCase(),
        password: values.password,
        gender: values.gender as SignUpWithOrganizationUserGender,
        phone: values?.phone,
        username: values.name,
        birthDate: formatedBirthday,
        incompleteFlow,
        organizationInfos: {
          teammates: {
            emails: teammates ?? [],
          },
          additionalInfos: {
            plataform: 'vtex',
            segment: values.organization_type,
            // resellersEstimate: Number(values.resellers),
          },
          organization: {
            contactEmail: values.contact_email,
            name: values.organization_name,
            document: cnpjOnlyNumbers,
            address: {
              address: values.street,
              cep: values.cep,
              city: values.city,
              neighbourhood: values.neighbourhood,
              number: values.number,
              state: values.state,
              complement: values.complement,
            },
          },
        },
      },
    };

    try {
      const { data } = await signUpWithOrganization({
        variables,
      });

      if (data) await handleOrganizationCreateAccountSucessfull();
    } catch (_) {
      toast.error('Ocorreu um erro ao finalizar o cadastro');
    }
  };

  const handleOrganizationCreateAccountSucessfull = async (): Promise<void> => {
    try {
      const { data } = await signIn({
        variables: {
          input: {
            email: values.email.toLowerCase(),
            password: values.password,
          },
        },
      });

      if (data) {
        login({
          id: data.signIn.user.id,
          token: data.signIn.token,
          email: data.signIn.user.email,
          username: data.signIn.user.username,
          cookieDays: 15,
          loginAnimation: true,
          redirect: true,
        });
      }
    } catch (_) {
      setLoading(false);
      history.push('/');
    }
  };

  const handleSubmitStepWithoutTeammates = (): Promise<void> => handleCreateOrganizationRequest([]);

  const handleSubmitStepWithTeammates = async (): Promise<void> => {
    await addValues({
      teammates,
    });

    await handleCreateOrganizationRequest(teammates);
  };

  return (
    <Flex sx={{ gap: '32px' }} flexDirection="column">
      <Spacer mt="32px" />

      <TitleForm>{t('hubly-dashboard.signup.admin.right-side.fifth-step.title')}</TitleForm>

      <Spacer mt="8px" />

      <Formik
        validationSchema={OrganizationInviteTeammatesSchema}
        initialValues={{ email: '' }}
        validateOnBlur={false}
        onSubmit={(values, formikHelpers): void =>
          handleAddTeammateEmail(values, formikHelpers.resetForm)
        }
      >
        {({ handleChange, handleBlur, handleSubmit, values, touched, errors }): JSX.Element => (
          <Flex sx={{ gap: '8px' }} alignItems="center">
            <Input
              label={t('hubly-dashboard.signup.admin.right-side.fifth-step.form.email.label')}
              placeholder={t(
                'hubly-dashboard.signup.admin.right-side.fifth-step.form.email.placeholder',
              )}
              onChange={handleChange}
              name="email"
              onBlur={handleBlur}
              value={values.email}
              error={touched.email && !!errors.email}
              helperText={touched.email ? errors.email : ''}
              fluid
            />
            <StyledButton onClick={(): void => handleSubmit()}>Convite</StyledButton>
          </Flex>
        )}
      </Formik>

      <Flex flexDirection="column" sx={{ gap: '8px' }}>
        {teammates.map(teammateMail => (
          <TeammateWrapper key={teammateMail}>
            <Avatar name={teammateMail} />
            <Typography as="span" color="neutralLowMedium" size="XS" fontWeight={500}>
              {teammateMail}
            </Typography>
            <RemoveTeammateIcon
              onClick={(): void => handleRemoveTeammateEmail(teammateMail)}
              iconId="trash"
            />
          </TeammateWrapper>
        ))}
      </Flex>

      <Flex flexDirection="column" sx={{ gap: '16px' }}>
        <CheckBox
          name="temsAndPolitics"
          value={false}
          label={
            (t(
              'hubly-dashboard.signup.admin.right-side.fifth-step.form.terms.text',
            ) as unknown) as string
          }
        />
        <Button
          onClick={handleSubmitStepWithTeammates}
          disabled={(!teammates.length && !values.teammates?.length) || loading}
          loading={loading && (values.teammates?.length ?? 0) > 0}
        >
          {t('hubly-dashboard.signup.admin.right-side.fifth-step.form.buttons.signup.text')}
        </Button>
      </Flex>

      <Button
        hierarchy="ghost"
        onClick={handleSubmitStepWithoutTeammates}
        disabled={loading}
        loading={loading && !values.teammates?.length}
      >
        {t('hubly-dashboard.signup.admin.right-side.fifth-step.form.buttons.skip.text')}
      </Button>
    </Flex>
  );
};

export default OrganizationRegistrationInviteTeammatesStep;
